"use client";

import { useEffect, useRef, useState } from "react";
import { ACHIEVEMENTS, readMine, useAchievements } from "./achievements";

const NAME_KEY = "nii-observer-name";
const NUM_KEY = "nii-observer-number";

// Удостоверение полевого наблюдателя: имя → карточка на canvas → скачать или поделиться.
// Ничего не отправляется на сервер: картинка собирается в браузере.
export default function Certificate({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const got = useAchievements();
  const [name, setName] = useState("");
  const [num, setNum] = useState(0);
  const [mine, setMine] = useState(0);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    try {
      setName(localStorage.getItem(NAME_KEY) || "");
      let n = Number(localStorage.getItem(NUM_KEY));
      if (!n) {
        n = 1000 + Math.floor(Math.random() * 8999);
        localStorage.setItem(NUM_KEY, String(n));
      }
      setNum(n);
    } catch {
      setNum(1287);
    }
    setMine(readMine());
    setNote("");
    dialog.current?.showModal();
  }, [open]);

  // рисуем карточку при каждом изменении
  useEffect(() => {
    const c = canvas.current;
    if (!open || !c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const W = 1200;
    const H = 750;
    c.width = W;
    c.height = H;
    ctx.fillStyle = "#16191c";
    ctx.fillRect(0, 0, W, H);
    // перелив шеи
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "rgba(46,125,99,0.55)");
    g.addColorStop(1, "rgba(107,74,138,0.55)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, 14);
    ctx.fillStyle = "#9aa3ab";
    ctx.font = "600 24px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText("НИИ «ПТЕНЕЦ» · ОТДЕЛ ПОЛЕВЫХ НАБЛЮДЕНИЙ", 64, 92);
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 64px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText("Удостоверение", 64, 176);
    ctx.fillText("полевого наблюдателя", 64, 246);
    ctx.fillStyle = "#f07a3e";
    ctx.font = "700 40px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText(`№ ${num}`, 64, 316);
    ctx.fillStyle = "#ffffff";
    ctx.font = "600 52px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText(name.trim() || "Позывной наблюдателя", 64, 420);
    ctx.fillStyle = "#9aa3ab";
    ctx.font = "400 28px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText(`Наблюдений: ${mine}   ·   Найдено птенцов: 0   ·   Достижений: ${got.length} из ${Object.keys(ACHIEVEMENTS).length}`, 64, 476);
    // значки ачивок
    let x = 64;
    let y = 540;
    ctx.font = "600 22px system-ui, -apple-system, Segoe UI, sans-serif";
    got.forEach((id) => {
      const t = ACHIEVEMENTS[id].title;
      const w = ctx.measureText(t).width + 36;
      if (x + w > W - 300) {
        x = 64;
        y += 52;
      }
      ctx.fillStyle = "#2b2f33";
      ctx.beginPath();
      ctx.roundRect(x, y - 30, w, 42, 21);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.fillText(t, x + 18, y);
      x += w + 10;
    });
    // печать
    ctx.save();
    ctx.translate(1010, 560);
    ctx.rotate(-0.18);
    ctx.strokeStyle = "#f07a3e";
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(0, 0, 110, 0, Math.PI * 2);
    ctx.stroke();
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, 92, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = "#f07a3e";
    ctx.textAlign = "center";
    ctx.font = "800 26px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText("ВЕРИМ", 0, -6);
    ctx.fillText("НА СЛОВО", 0, 26);
    ctx.restore();
    ctx.textAlign = "left";
    ctx.fillStyle = "#5e666d";
    ctx.font = "400 20px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText("Действительно на всех карнизах. Голубями не признаётся.", 64, H - 48);
  }, [open, name, num, mine, got]);

  const save = (v: string) => {
    setName(v);
    try {
      localStorage.setItem(NAME_KEY, v);
    } catch {
      /* имя только для этой карточки */
    }
  };

  const toBlob = () => new Promise<Blob | null>((r) => canvas.current?.toBlob(r, "image/png"));
  const download = async () => {
    const blob = await toBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `udostoverenie-nablyudatelya-${num}.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setNote("Сохранено. Покажите охране чердака.");
  };
  const share = async () => {
    const blob = await toBlob();
    const text = `Я полевой наблюдатель № ${num} НИИ «Птенец». Птенцов найдено: 0.`;
    try {
      const file = blob ? new File([blob], `nablyudatel-${num}.png`, { type: "image/png" }) : null;
      if (file && navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], text, url: location.origin });
      else if (navigator.share) await navigator.share({ text, url: location.origin });
      else {
        await navigator.clipboard.writeText(`${text} ${location.origin}`);
        setNote("Текст и ссылка скопированы.");
      }
    } catch {
      /* пользователь закрыл окно «Поделиться» */
    }
  };

  return (
    <dialog
      ref={dialog}
      className="modal cert"
      aria-labelledby="cert-title"
      onClose={onClose}
      onClick={(e) => e.target === e.currentTarget && dialog.current?.close()}
    >
      <button type="button" className="x" aria-label="Закрыть" onClick={() => dialog.current?.close()}>
        ×
      </button>
      <p className="eyebrow label">Отдел кадров института</p>
      <h2 id="cert-title">Удостоверение наблюдателя</h2>
      <div className="field">
        <label htmlFor="cert-name">Позывной наблюдателя</label>
        <input id="cert-name" value={name} maxLength={28} placeholder="Например: Сизый-7" onChange={(e) => save(e.target.value)} />
      </div>
      <canvas ref={canvas} className="cert__img" role="img" aria-label={`Удостоверение наблюдателя № ${num}${name ? `, ${name}` : ""}`} />
      <div className="actions">
        <button type="button" className="btn btn-p label" onClick={download}>
          Скачать
        </button>
        <button type="button" className="btn btn-o label" onClick={share}>
          Поделиться
        </button>
      </div>
      {note && (
        <p className="cert__note" role="status">
          {note}
        </p>
      )}
    </dialog>
  );
}
